import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { SimpleGrid, Box, HStack, Text } from "@chakra-ui/react";

const layouts = {
  time: {
    title: "Time per Run",
    xaxis: { title: "" },
    yaxis: { title: "Time (min)" },
    showlegend: false
  },
  distance: {
    title: "Distance per Run",
    xaxis: { title: "" },
    yaxis: { title: "Distance (km)" },
    showlegend: false
  },
  speed: {
    title: "Average Speed per Run",
    xaxis: { title: "" },
    yaxis: { title: "Speed (min/km)" },
    showlegend: false
  },
  heart: {
    title: "Average Heart Rate per Run",
    xaxis: { title: "" },
    yaxis: { title: "Heart Rate (bpm)" },
    showlegend: false
  }
};

// Map sport types to icon component names
function getSportIconName(sport) {
  const sportIcons = {
    'indoor_climbing': 'climb',
    'indoor_cycling': 'bike',
    'cycling': 'bike',
    'swimming': 'swim',
    'running': 'run',
    'walking': 'walk',
    'hiking': 'hike',
    'yoga': 'yoga',
    'strength_training': 'weight',
    'gym': 'fitness',
    'default': 'fitness'
  };
  return sportIcons[sport] || sportIcons['default'];
}

// Map sport types to PNG image paths
function getSportImage(sport) {
  const sportImages = {
    'indoor_climbing': '/images/climb.png',
    'indoor_cycling': '/images/cycling.png',
    'cycling': '/images/cycling.png',
    'swimming': '/images/swimming.png',
    'running': '/images/shoes.png',
    'walking': '/images/shoes.png',
    'hiking': '/images/shoes.png',
    'yoga': '/images/shoes.png',
    'strength_training': '/images/strength.png',
    'gym': '/images/strength.png',
    'default': '/images/shoes.png'
  };
  return sportImages[sport] || sportImages['default'];
}

// Calculate linear regression for trend line
function calculateTrend(xData, yData) {
  if (!xData || !yData || xData.length < 2) return null;
  
  // Convert dates to numeric values (days since first date)
  const firstDate = new Date(xData[0]);
  const xNumeric = xData.map(date => {
    const currentDate = new Date(date);
    return (currentDate - firstDate) / (1000 * 60 * 60 * 24); // days
  });
  
  const n = xNumeric.length;
  const sumX = xNumeric.reduce((a, b) => a + b, 0);
  const sumY = yData.reduce((a, b) => a + b, 0);
  const sumXY = xNumeric.reduce((sum, x, i) => sum + x * yData[i], 0);
  const sumXX = xNumeric.reduce((sum, x) => sum + x * x, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  const trendY = xNumeric.map(x => slope * x + intercept);
  
  return {
    x: xData,
    y: trendY,
    slope,
    intercept
  };
}

// Function to calculate y-axis range from data
function calculateYRange(data) {
  if (!data || !Array.isArray(data)) return [0, 100];
  
  let allYValues = [];
  data.forEach(trace => {
    if (trace.y && Array.isArray(trace.y)) {
      allYValues = allYValues.concat(trace.y.filter(val => val != null && !isNaN(val)));
    }
  });
  
  if (allYValues.length === 0) return [0, 100];
  
  const minY = Math.min(...allYValues);
  const maxY = Math.max(...allYValues);
  const range = maxY - minY;
  const padding = range * 0.1; // 10% padding
  
  return [Math.max(0, minY - padding), maxY + padding];
}

// Function to create event shapes for background rectangles (only for date ranges)
function createEventShapes(events, yRange) {
  if (!events || !events.data) return [];
  
  const shapes = [];
  
  events.data.forEach(event => {
    if (event.start_date && event.end_date) {
      // Event with date range - draw rectangle
      shapes.push({
        type: 'rect',
        xref: 'x',
        yref: 'y',
        x0: event.start_date,
        y0: yRange[0],
        x1: event.end_date,
        y1: yRange[1],
        fillcolor: event.color,
        opacity: 0.2,
        layer: 'below',
        line: {
          width: 0
        }
      });
    }
    // Training events (single date) will be shown as icons via traces, not shapes
  });
  
  return shapes;
}

// Function to create event traces for hover info and images
function createEventHoverTraces(events, yRange) {
  if (!events || !events.data) return { traces: [], images: [] };
  
  const eventTraces = [];
  const eventImages = [];
  
  events.data.forEach(event => {
    let hoverX, hoverY, hoverTemplate, markerConfig;
    
    if (event.start_date && event.end_date) {
      // Event with date range - invisible marker at center
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;
      
      const midDate = new Date((startDate.getTime() + endDate.getTime()) / 2);
      hoverX = midDate.toISOString().split('T')[0];
      hoverY = (yRange[0] + yRange[1]) / 2;
      hoverTemplate = `<b>📅 ${event.title}</b><br>` +
                      `Type: ${event.type.charAt(0).toUpperCase() + event.type.slice(1)}<br>` +
                      `Period: ${event.start_date} to ${event.end_date}<br>` +
                      `${event.description ? `Description: ${event.description}<br>` : ''}` +
                      '<extra></extra>';
      markerConfig = {
        size: 20,
        color: event.color,
        opacity: 0, // Invisible for date ranges
        line: { width: 0 }
      };
      
      eventTraces.push({
        x: [hoverX],
        y: [hoverY],
        type: 'scatter',
        mode: 'markers',
        marker: markerConfig,
        showlegend: false,
        hovertemplate: hoverTemplate,
        hoverinfo: 'text',
        name: event.title
      });
    } else if (event.date) {
      // Single-day training event - visible icon at top
      const eventDate = new Date(event.date);
      if (isNaN(eventDate.getTime())) return;
      
      const sportImage = getSportImage(event.sport);
      hoverX = event.date;
      // Position slightly below the top (90% of the range) so icon is fully visible
      const yPadding = (yRange[1] - yRange[0]) * 0.1;
      hoverY = yRange[1] - yPadding;
      hoverTemplate = `<b>${event.title}</b><br>` +
                      `Sport: ${event.sport ? event.sport.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}<br>` +
                      `Date: ${event.date}<br>` +
                      `${event.description ? event.description : ''}<br>` +
                      '<extra></extra>';
      
      // Create invisible marker for hover
      markerConfig = {
        size: 15,
        color: 'transparent',
        opacity: 0.01,
        line: { width: 0 }
      };
      
      eventTraces.push({
        x: [hoverX],
        y: [hoverY],
        type: 'scatter',
        mode: 'markers',
        marker: markerConfig,
        showlegend: false,
        hovertemplate: hoverTemplate,
        hoverinfo: 'text',
        name: event.title
      });
      
      // Add image to layout images array
      eventImages.push({
        source: sportImage,
        xref: 'x',
        yref: 'y',
        x: hoverX,
        y: hoverY,
        sizex: 2 * 24 * 60 * 60 * 1000, // Size in milliseconds (1.25 days)
        sizey: (yRange[1] - yRange[0]) * 0.1, // 4% of y-range
        xanchor: 'center',
        yanchor: 'middle',
        sizing: 'contain',
        layer: 'above'
      });
    }
  });
  
  return { traces: eventTraces, images: eventImages };
}

const legendItems = [
  { color: "orange", label: "Run Intervals" },
  { color: "grey", label: "Walk Intervals" },
  { color: "rgba(255, 140, 0, 0.5)", label: "Run Trend", isDashed: true },
  { color: "rgba(128, 128, 128, 0.5)", label: "Walk Trend", isDashed: true }
];

// Helper function to get initial x-axis range (last 3 months)
function getInitialXRange(data) {
  if (!data || data.length === 0) return null;

  // Find all dates across all traces
  let allDates = [];
  data.forEach(trace => {
    if (trace.x && trace.x.length > 0) {
      allDates = allDates.concat(trace.x);
    }
  });

  if (allDates.length === 0) return null;

  // Use today's date as the end date
  const latestDate = new Date();

  // Calculate date 3 months ago
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(latestDate.getMonth() - 3);

  return [threeMonthsAgo.toISOString().split('T')[0], latestDate.toISOString().split('T')[0]];
}

// Helper to add plotly trace info for traces with only x, y, and interval_type
function decorateTraces(data, events) {
  if (!data) return { traces: [], trendInfo: {}, yRange: [0, 100], shapes: [], images: [], initialXRange: null };

  // Don't filter data - pass all of it to Plotly
  const fullData = data;

  const decoratedTraces = [];
  const trendInfo = { run: null, walk: null };
  const initialXRange = getInitialXRange(fullData);

  fullData.forEach(trace => {
    let color = trace.interval_type === "run" ? "orange" : "grey";
    let name = trace.interval_type === "run" ? "Run Intervals" : "Walk Intervals";

    // Add main data trace
    decoratedTraces.push({
      ...trace,
      type: "scatter",
      mode: "lines+markers",
      name,
      marker: { color, size: 6 },
      line: { color, width: 2 }
    });

    // Calculate and add trend line
    if (trace.x && trace.y && trace.x.length > 1) {
      const trend = calculateTrend(trace.x, trace.y);
      if (trend) {
        const trendColor = trace.interval_type === "run" ? "rgba(255, 140, 0, 0.5)" : "rgba(128, 128, 128, 0.5)";
        const trendName = trace.interval_type === "run" ? "Run Trend" : "Walk Trend";

        // Store trend info for title display
        trendInfo[trace.interval_type] = trend;

        decoratedTraces.push({
          x: trend.x,
          y: trend.y,
          type: "scatter",
          mode: "lines",
          name: trendName,
          line: {
            color: trendColor,
            width: 2,
            dash: "dash"
          },
          hovertemplate: `<b>${trendName}</b><br>` +
                        'Date: %{x}<br>' +
                        'Trend Value: %{y:.2f}<br>' +
                        `Slope: ${trend.slope.toFixed(4)}/day<br>` +
                        '<extra></extra>'
        });
      }
    }
  });

  // Calculate y-axis range from the data
  const yRange = calculateYRange(decoratedTraces);

  // Create event shapes and hover traces
  const shapes = createEventShapes(events, yRange);
  const eventHoverData = createEventHoverTraces(events, yRange);

  // Add event hover traces to the main traces
  decoratedTraces.push(...eventHoverData.traces);

  return {
    traces: decoratedTraces,
    trendInfo,
    yRange,
    shapes,
    images: eventHoverData.images,
    initialXRange
  };
}

// Helper function to format slope for display
function formatSlope(slope, metricType) {
  if (!slope) return "";
  
  const absSlope = Math.abs(slope);
  let formattedSlope;
  let unit;
  
  switch (metricType) {
    case 'distance':
      formattedSlope = (absSlope * 365).toFixed(2); // km per year
      unit = 'km/year';
      break;
    case 'time':
      formattedSlope = (absSlope * 365).toFixed(1); // minutes per year
      unit = 'min/year';
      break;
    case 'speed':
      formattedSlope = (absSlope * 365).toFixed(2); // min/km per year
      unit = 'min/km/year';
      break;
    case 'heart':
      formattedSlope = (absSlope * 365).toFixed(1); // bpm per year
      unit = 'bpm/year';
      break;
    default:
      formattedSlope = absSlope.toFixed(4);
      unit = '/day';
  }
  
  const direction = slope > 0 ? '↗' : '↘';
  return `${direction} ${formattedSlope} ${unit}`;
}

// Helper function to create dynamic layout with slope info and events
function createLayoutWithSlope(baseLayout, trendInfo, metricType, yRange, shapes, images, initialXRange) {
  let slopeText = "";

  if (trendInfo.run && trendInfo.run.slope) {
    const runSlope = formatSlope(trendInfo.run.slope, metricType);
    slopeText += `Run: ${runSlope}`;
  }

  if (trendInfo.walk && trendInfo.walk.slope) {
    const walkSlope = formatSlope(trendInfo.walk.slope, metricType);
    if (slopeText) slopeText += " | ";
    slopeText += `Walk: ${walkSlope}`;
  }

  return {
    ...baseLayout,
    title: {
      text: `${baseLayout.title}${slopeText ? `<br><span style="font-size: 12px; color: #666;">${slopeText}</span>` : ''}`,
      font: { size: 16 }
    },
    xaxis: {
      ...baseLayout.xaxis,
      range: initialXRange, // Set initial view to last 3 months
      fixedrange: false // Allow x-axis zoom
    },
    yaxis: {
      ...baseLayout.yaxis,
      range: yRange,
      fixedrange: true // Prevent y-axis zoom
    },
    shapes: shapes,
    images: images || [],
    dragmode: 'pan', // Enable drag to pan
    hovermode: 'closest',
    margin: {
      l: 50,
      r: 20,
      t: 60,
      b: 30
    }
  };
}

function ProgressCharts() {
  const [charts, setCharts] = useState({});
  const [events, setEvents] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("/data/time.json").then(res => res.json()),
      fetch("/data/distance.json").then(res => res.json()),
      fetch("/data/speed.json").then(res => res.json()),
      fetch("/data/heart.json").then(res => res.json()),
      fetch("/data/events.json").then(res => res.json()).catch(() => ({ data: [] })) // Fallback if events.json doesn't exist
    ]).then(([time, distance, speed, heart, eventsData]) => {
      setCharts({ time, distance, speed, heart });
      setEvents(eventsData);
    });
  }, []);

  const metrics = [
    { key: "time", title: "Time per Run" },
    { key: "distance", title: "Distance per Run" },
    { key: "speed", title: "Average Speed per Run" },
    { key: "heart", title: "Average Heart Rate per Run" }
  ];

  return (
    <>
      {/* Legend */}
      <HStack spacing={6} mb={4}>
        {legendItems.map(item => (
          <HStack key={item.label} spacing={2}>
            <Box
              boxSize="16px"
              bg={item.color}
              borderRadius="full"
              border={item.isDashed ? "2px dashed" : "none"}
              borderColor={item.isDashed ? item.color : "transparent"}
              backgroundColor={item.isDashed ? "transparent" : item.color}
            />
            <Text fontSize="sm">{item.label}</Text>
          </HStack>
        ))}

        {/* Events Legend */}
        {events && events.data && events.data.length > 0 && (
          <>
            <Text fontSize="sm" fontWeight="bold" color="gray.600">Events:</Text>
            {Array.from(new Set(events.data.map(e => e.type))).map(eventType => {
              const event = events.data.find(e => e.type === eventType);
              return (
                <HStack key={eventType} spacing={2}>
                  <Box
                    boxSize="16px"
                    bg={event.color}
                    opacity={0.3}
                    borderRadius="sm"
                  />
                  <Text fontSize="sm" textTransform="capitalize">{eventType}</Text>
                </HStack>
              );
            })}
          </>
        )}
      </HStack>
      
      <SimpleGrid columns={[1, 2]} spacing={0}>
        {metrics.map(metric => {
          let data = charts[metric.key];
          if (!data) return null;
          // Support both {data: [...]} and [...] formats
          if (data.data) data = data.data;
          const result = decorateTraces(data, events);
          const dynamicLayout = createLayoutWithSlope(
            layouts[metric.key],
            result.trendInfo,
            metric.key,
            result.yRange,
            result.shapes,
            result.images,
            result.initialXRange
          );

          return (
            <Box key={metric.key}>
              <Plot
                data={result.traces}
                layout={dynamicLayout}
                style={{ width: "100%", height: "350px" }}
                config={{
                  responsive: true,
                  scrollZoom: true, // Enable scroll/pinch to zoom
                  displayModeBar: false, // Hide the toolbar
                  doubleClick: 'reset' // Double-click to reset zoom
                }}
              />
            </Box>
          );
        })}
      </SimpleGrid>
    </>
  );
}

export default ProgressCharts;